import { useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useSelector } from "react-redux";
import { transformFormData } from "../../DataStoreHelpers/dataStoreHelpers"; // adjust import as needed
import { useDispatch } from "react-redux";
import {
  setLocalMergedOutputData,
  updateLocalMergedOutputData,
} from "../../../../../features/dataStoreReqSlice";

const EditOutput = () => {
  const { editQueryFormDataInput, editQueryFormData, localMergedOutputData } =
    useSelector((state: any) => state.dataStoreReq);

  const transformedInputs = transformFormData(editQueryFormDataInput);
  const dispatch = useDispatch();

  const editableOutputData = Object.entries(
    editQueryFormData?.output?.properties
  ).map(([key, value]: any) => ({
    key, // original key like "Actual", "Gap"
    ...value,
  }));

  const newOutputData = useSelector(
    (state: any) => state.dataStoreReq.editableOutputValues
  );

  useEffect(() => {
    if (localMergedOutputData.length === 0) {
      const merged = newOutputData.map((newData: any) => {
        const editableField = editableOutputData.find(
          (field: any) => field.key === newData.name
        );

        return {
          ...newData,
          title:
            editableField?.title ||
            (newData.title !== "null" ? newData.title : ""),
          description:
            editableField?.description ||
            (newData.description !== "null" ? newData.description : ""),
          order:
            editableField?.order ||
            (newData.order !== "null" ? newData.order : ""),
        };
      });

      dispatch(setLocalMergedOutputData(merged));
    }
    // eslint-disable-next-line
  }, []);

  const [editedRowIndex, setEditedRowIndex] = useState<number | null>(null);
  const [, setEditedOrderValue] = useState<string>("");

  const handleOutputChange = (id: number, key: string, value: string) => {
    dispatch(updateLocalMergedOutputData({ id, key, value }));
  };

  // Handle blur event
  const handleFocus = (index: number, currentValue: string) => {
    setEditedRowIndex(index);
    setEditedOrderValue(currentValue);
  };


  const handleBlur = (index: number) => {
    if (editedRowIndex === index) {
      // Get the current value from localMergedOutputData instead of using editedOrderValue
      const currentValue = localMergedOutputData[index].order;

      const newData = [...localMergedOutputData];
      newData[index] = { ...newData[index], order: currentValue };

      // Sort after setting new order
      newData.sort((a, b) => {
        const aOrder = parseInt(a.order ?? "0", 10);
        const bOrder = parseInt(b.order ?? "0", 10);
        return aOrder - bOrder;
      });

      dispatch(setLocalMergedOutputData(newData));
      setEditedRowIndex(null);
      setEditedOrderValue("");
    }
  };





  return (
    <Box mt={2}>
      <Accordion
        sx={{
          minHeight: "48px",
          "& .MuiAccordionSummary-content.Mui-expanded": {
            margin: "0px",
          },
        }}
      >
        <AccordionSummary
          sx={{
            minHeight: "48px !important",
          }}
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1-content"
          id="panel1-header"
        >
          <Typography component="span">Input Fields Review</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box
            sx={{
              overflowX: "auto",
              display: "flex",
              scrollbarWidth: "thin",
            }}
          >
            {transformedInputs.map((input: any, i: any) => (
              <Box
                key={i}
                mb={1}
                sx={{
                  minWidth: "100%",
                  borderRight: "1px solid #E0E0E0",
                }}
                mx={1}
              >
                <Stack
                  flexDirection={"row"}
                  alignItems={"center"}
                  justifyContent={"space-between"}
                >
                  <Typography component="span">{input.title}</Typography>
                  <FormGroup>
                    <Stack flexDirection={"row"}>
                      {input.fields[4] && (
                        <FormControlLabel
                          control={
                            <Checkbox
                              color="secondary"
                              checked={input.fields[4].value}
                              size="small"
                              disabled
                            />
                          }
                          label="UNIQ"
                        />
                      )}
                      {input.fields[5] && (
                        <FormControlLabel
                          control={
                            <Checkbox
                              color="secondary"
                              checked={input.fields[5].value}
                              size="small"
                              disabled
                            />
                          }
                          label="Not Mutable"
                        />
                      )}
                    </Stack>
                  </FormGroup>
                </Stack>
                <Grid container spacing={1} mt={1}>
                  {input.fields.map((field: any, index: any) => (
                    <Grid item xs={4} key={index}>
                      <TextField
                        label={field.label}
                        disabled
                        fullWidth
                        variant="standard"
                        value={field.value}
                        sx={{
                          "& .MuiInput-underline:before": {
                            borderBottomStyle: "none !important",
                          },
                        }}
                      />
                    </Grid>
                  ))}
                  <Grid item xs={4}>
                    <TextField
                      label={"Description"}
                      disabled
                      fullWidth
                      variant="standard"
                      value={input.description}
                      sx={{
                        "& .MuiInput-underline:before": {
                          borderBottomStyle: "none !important",
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>
            ))}
          </Box>
        </AccordionDetails>
      </Accordion>

      <Accordion
        sx={{
          minHeight: "48px",
          "& .MuiAccordionSummary-content.Mui-expanded": {
            margin: "0px",
          },
        }}
        defaultExpanded
      >
        <AccordionSummary
          sx={{
            minHeight: "48px !important",
          }}
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel2-content"
          id="panel2-header"
        >
          <Typography component="span">Output Fields Review</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box>
            {localMergedOutputData.map((field: any, index: number) => (
              <div key={field.key}>
                <Stack
                  flexDirection={"row"}
                  alignItems={"center"}
                  justifyContent={"space-between"}
                  mt={2}
                >
                  <Typography>{field.name}</Typography>
                  <FormGroup>
                    <Stack flexDirection={"row"}>
                      <FormControlLabel
                        control={<Checkbox color="secondary" disabled />}
                        label="UNIQ"
                      />
                      <FormControlLabel
                        control={<Checkbox color="secondary" disabled />}
                        label="Not Mutable"
                      />
                    </Stack>
                  </FormGroup>
                </Stack>
                <Grid container spacing={2} mt={2}>
                  <Grid item xs={6}>
                    <TextField
                      id={`${field.key}-title`}
                      label="Title"
                      fullWidth
                      size="small"
                      color="secondary"
                      placeholder="Please enter Title"
                      value={field.title ?? ""}
                      onChange={(e) =>
                        handleOutputChange(field.id, "title", e.target.value)
                      }
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Description"
                      placeholder="Please enter Description"
                      fullWidth
                      size="small"
                      color="secondary"
                      value={field.description ?? ""}
                      onChange={(e) =>
                        handleOutputChange(
                          field.id,
                          "description",
                          e.target.value
                        )
                      }
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={2} mt={2}>
                  <Grid item xs={4}>
                    <TextField
                      label="Type"
                      value={field.type}
                      fullWidth
                      size="small"
                      color="secondary"
                      disabled
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      label="Format"
                      fullWidth
                      size="small"
                      color="secondary"
                      disabled
                      value=""
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      id={`${field.key}-order`}
                      label="Order"
                      fullWidth
                      size="small"
                      color="secondary"
                      placeholder="Please enter Order"
                      value={field.order ?? ""}
                      onChange={(e) =>
                        handleOutputChange(field.id, "order", e.target.value)
                      }
                      onFocus={() => handleFocus(index, field.order ?? "")}
                      onBlur={() => handleBlur(index)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>
              </div>
            ))}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default EditOutput;
